import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../api/api';
import { getTokenData } from "src/utils/api";
import { LOADING_STATUSES } from '../../../constants/statuses';
import { USER_LOCAL_STORAGE } from '../../../constants/user';
import { isResponseSuccess } from '../../../utils/api';

export const loginUser = createAsyncThunk(
  'user/login',
  async (userData, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).USER.AUTH.login({
        ...userData,
      });
      if (isResponseSuccess(response.status)) {
        return {
          userData: response.data,
          headers: {
            authorization: response.headers.authorization,
            xRefreshToken: response.headers['x-refresh-token'],
          },
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

const initialState = {
  entity: {},
  headersTokens: {},
  status: LOADING_STATUSES.IDLE,
  error: null,
  env: {},
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUser(state) {
      state.entity = {};
      state.status = LOADING_STATUSES.IDLE;
      state.error = null;
    },
    setUser(state, action) {
      state.entity = action.payload.user;
      state.headersTokens = action.payload.headers;
    },
    setEnv: (state, action) => {
      state.env = action.payload;
    },
    updateHeaderTokens(state, action) {
      state.headersTokens = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.entity = action.payload?.userData?.user || {};
        state.headersTokens = action.payload?.headers || {};
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = LOADING_STATUSES.FAILED;
        state.error = action.payload;
      });
  },
});

export const {
  resetUser,
  setUser,
  setEnv,
  updateHeaderTokens,
} = userSlice.actions;

export default userSlice.reducer;

export const logOut = createAsyncThunk(
  'user/logout',
  async ({
    urlParams = {
      current_session: true,
    },
    fast = false,
  }, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).USER.AUTH.logOut(urlParams);
      if (fast) {
        dispatch(resetUser());
        return true;
      }
      if (isResponseSuccess(response.status)) {
        dispatch(resetUser());
        return true;
      }
      return false;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const refresh = createAsyncThunk(
  'user/refresh',
  async ({}, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).USER.AUTH.refreshToken(
        {},
        {
          'x-refresh-token': getState().userReducer.headersTokens.xRefreshToken,
        },
      );
      if (isResponseSuccess(response.status)) {
        const headers = {
          authorization: response.headers.authorization,
          xRefreshToken: response.headers['x-refresh-token'],
        };
        dispatch(updateHeaderTokens(headers));
        localStorage.setItem(USER_LOCAL_STORAGE.HEADERS_USER_TOKENS, JSON.stringify(headers));
        return headers;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const getEnvAction = createAsyncThunk(
  'user/getEnv',
  async ({}, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).BASIC.ENV.load();
      // A misrouted env request can be answered by the SPA catch-all with
      // index.html (status 200, HTML string) — never let a non-object into the store.
      if (isResponseSuccess(response.status) && typeof response.data === 'object' && response.data !== null) {
        dispatch(setEnv(response.data));
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);
